import React, { useState, useEffect, useRef } from "react";
import * as sdk from "matrix-js-sdk";
import { motion } from "framer-motion";
import "../styles/ChatOverlay.css"; // Assuming this exists for custom styles

function ChatOverlay() {
    const [roomId, setRoomId] = useState("");
    const [roomCreated, setRoomCreated] = useState(false);
    const [client, setClient] = useState(null);
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const [liveTimeLine, setLiveTimeLine] = useState([]);
    const accessToken = process.env.REACT_APP_MATRIX_ACCESS_TOKEN; // Unified token
    const userId = process.env.REACT_APP_MATRIX_USER_ID;
    const homeserverUrl = process.env.REACT_APP_MATRIX_HOMESERVER;
    const invitedUser = process.env.REACT_APP_INVITED_USER; // Consistent domain

    const messagesEndRef = useRef(null);

    const fetchMessages = async () => {
        if (!roomId) return; // Don't fetch if no room

        try {
            const response = await fetch(
                `${homeserverUrl}/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/messages?dir=b&limit=10`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                console.error("Error fetching messages:", response.status, await response.text());
                return;
            }

            const data = await response.json();
            const newMessages = data.chunk
                .filter((event) => event.type === "m.room.message") // Only chat messages
                .map((event) => ({
                    id: event.event_id,
                    sender: event.sender,
                    body: event.content.body,
                }))
                .reverse(); // Reverse to chronological order (since dir=b fetches newest first)

            // Update state: Merge without duplicates
            setMessages((prev) => {
                const merged = [...prev];
                newMessages.forEach((newMsg) => {
                    if (!merged.some((msg) => msg.id === newMsg.id)) {
                        merged.push(newMsg);
                    }
                });
                // Optional: Sort by timestamp if needed (assuming origin_server_ts)
                return merged.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)); // Add timestamp if available
            });
        } catch (error) {
            console.error("Fetch error:", error);
        }
    };
    // Initialize Matrix client
    useEffect(() => {
        const matrixClient = sdk.createClient({
            baseUrl: homeserverUrl,
            accessToken,
            userId,
        });
        setClient(matrixClient);
        matrixClient.startClient();
        return () => {
            matrixClient.stopClient();
        };
    }, []);


    useEffect(() => {
        if (!roomCreated) return;

        fetchMessages(); // Initial fetch

        const intervalId = setInterval(fetchMessages, 3000);

        return () => clearInterval(intervalId); // Cleanup
    }, [roomCreated, roomId]); // Re-run if room changes

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const createRoom = async () => {
        if (!client) return;
        try {
            const guestUsername = await getGuestUsername();
            const room = await client.createRoom({
                invite: [invitedUser],
                is_direct: true,
                preset: "trusted_private_chat",
                visibility: "private",
                name: guestUsername,
            });
            setRoomId(room.room_id);
            setRoomCreated(true);
        } catch (error) {
            console.error("Error creating room:", error);
        }
    };

    const sendMessageHandler = async () => {
        if (!message.trim() || !client || !roomId) return;

        // Optimistic update: Add message locally first
        //const optimisticMsg = {
        //     id: Date.now().toString(), // Temporary ID
        //     sender: userId,
        //     body: message,
        // };
        // setMessages((prev) => [...prev, optimisticMsg]);
        setMessage("");

        try {
            await client.sendMessage(roomId, {
                msgtype: "m.text",
                body: message,
            });
        } catch (error) {
            console.error("Error sending message:", error);
            // Optionally remove optimistic message on failure
        }
    };

    // Helper to get custom datetime
    function getCustomDatetime() {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, "0");
        const hours = pad(now.getHours());
        const minutes = pad(now.getMinutes());
        const day = pad(now.getDate());
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December",
        ];
        const month = monthNames[now.getMonth()];
        const year = now.getFullYear();
        return `${hours}:${minutes} ${day} ${month} ${year}`;
    }

    // Helper to get guest username (IP + datetime)
    async function getGuestUsername() {
        const datetime = getCustomDatetime();
        const ipRes = await fetch("https://whoami.espobuilt.com/");
        const ipData = await ipRes.json();
        return `${ipData.ip} ${datetime}`;
    }

    // Send message on Enter key
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessageHandler();
        }
    };
    const handleSend = (e) => {
        sendMessageHandler();
    }
    return (
        <motion.div
            initial={{ opacity: 0, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 1.5, ease: [0.2, 0, 0.2, 1] }}
            style={{
                position: "fixed",
                bottom: 18,
                right: 18,
                background: "#fff",
                zIndex: 1000,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                borderRadius: 8,
            }}
        >
            <div id="chat">
                {/* Header */}
                <div
                    style={{
                        background: "#0078FF",
                        color: "#fff",
                        padding: "16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <span id="chat-s-ekipa-na-espobult">Чат с екипа на EspoBuilt!</span>
                </div>

                {roomCreated ? (
                    <div style={{ padding: "12px", borderTop: "1px solid #eee" }}>
                        {/* Messages list (above input) */}
                        <div
                            id="messages"
                            style={{
                                maxHeight: 400,
                                overflowY: "auto",
                                padding: 16,
                                display: "flex",
                                flexDirection: "column", // Latest at bottom
                            }}
                        >
                            {messages.map((msg) => (
                                <div
                                    key={msg.id}
                                    style={{
                                        margin: "8px 0",
                                        padding: "8px 12px",
                                        borderRadius: 16,
                                        color: "black",
                                        maxWidth: "70%",
                                        alignSelf: msg.sender === userId ? "flex-end" : "flex-start",
                                    }}
                                >
                                    <b style={{ fontSize: 12 }}>{msg.sender}</b>
                                    <div>{msg.body}</div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} /> {/* For scrolling */}
                        </div>

                        {/* Input */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: "235px" }}>
                            <input
                                type="text"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Напишете съобщение.."
                                style={{
                                    flex: 1,
                                    padding: "10px",
                                    borderRadius: "8px",
                                    border: "1px solid black",
                                    outline: "none",
                                }}
                            />
                            <button
                                onClick={handleSend} // You'll need to define this function
                                style={{
                                    padding: "10px",
                                    backgroundColor: "blue",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "40px",
                                    height: "40px",
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="22" y1="2" x2="11" y2="13"></line>
                                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                </svg>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div>
                            <a id="dobre-doshli-text">Добре дошли! Как можем да Ви бъдем полезни?</a>
                        </div>
                        <button
                            id="start-chat-btn"
                            onClick={createRoom}
                            aria-label="Start chat"
                        >
                            Започни чат
                        </button>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

export default ChatOverlay;